import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Grid, GridColumn, ModalFooter, Spinner } from 'telia-front-react';

import InvoiceSettingsChangeLogStore from '../../../stores/InvoiceSettingsChangeLogStore';
import InvoiceSettingsModalStore from '../../../stores/InvoiceSettingsModalStore';
import InvoiceSettingsPanelStore from '../../../stores/InvoiceSettingsPanelStore';
import { ITranslateStore } from '../../../stores/TranslateStore';
import { scrollToTop } from '../../../util/ScrollUtils';
import ButtonWithAnalytics from '../../common/ButtonWithAnalytics';
import ClosableModal from '../../common/ClosableModal';
import LinkButton from '../../common/LinkButton';
import InfoNotice from '../notices/InfoNotice';
import InvoiceChangeLogNotice from '../notices/InvoiceChangeLogNotice';
import WarningNotice from '../notices/WarningNotice';

import EInvoiceSuppliersDataStore from '../../../stores/data/EInvoiceSuppliersDataStore';
import InvoiceSettingsPanel from './InvoiceSettingsPanel';

interface IInvoiceSettingsModalProps {
  invoiceSettingsModalStore?: InvoiceSettingsModalStore;
  invoiceSettingsPanelStore?: InvoiceSettingsPanelStore;
  invoiceSettingsChangeLogStore?: InvoiceSettingsChangeLogStore;
  eInvoiceSuppliersDataStore?: EInvoiceSuppliersDataStore;
  translateStore?: ITranslateStore;
}

@inject('invoiceSettingsModalStore', 'invoiceSettingsPanelStore', 'invoiceSettingsChangeLogStore', 'eInvoiceSuppliersDataStore', 'translateStore')
@observer
export default class InvoiceSettingsModal extends React.Component<IInvoiceSettingsModalProps> {
  private async onSaveClick() {
    const result = await this.props.invoiceSettingsModalStore.saveInvoiceSettings();
    if (result === 'save-error') {
      scrollToTop();
    }
  }

  public render(): JSX.Element {
    return (
      <ClosableModal
        gaPage={this.props.invoiceSettingsModalStore.gaPageLabel}
        gaGroup="Invoice settings"
        className="nls_protected"
        size="6-col"
        errorNoticeTranslationKey="invoices.invoice-settings-modal.failed-to-render"
      >
        {this.renderModalContent()}
      </ClosableModal>
    );
  }

  public renderModalContent(): JSX.Element | JSX.Element[] {
    const translate = this.props.translateStore.translate;

    if (
      this.props.invoiceSettingsModalStore.invoiceSettingsLoadedWithError ||
      this.props.eInvoiceSuppliersDataStore.eInvoiceSuppliers.isFinishedWithError ||
      this.props.invoiceSettingsPanelStore.status === 'failed'
    ) {
      return <WarningNotice translationKey="invoices.invoice-settings-modal.failed-to-render" />;
    }

    if (this.props.invoiceSettingsPanelStore.status === 'loading') {
      return <Spinner />;
    }

    if (this.props.invoiceSettingsPanelStore.status === 'not-found') {
      return <InfoNotice translationKey="invoices.invoice-settings-modal.reference-number-not-found" />;
    }

    if (this.props.invoiceSettingsPanelStore.invoiceSettings.state === 'UPDATING') {
      return <InfoNotice translationKey="invoices.invoice-settings-modal.updating-currently" />;
    }

    const panelStore = this.props.invoiceSettingsPanelStore;
    const modalStore = this.props.invoiceSettingsModalStore;

    return [
      <InvoiceSettingsPanel
        errorNotice={
          panelStore.ruleViolation ? (
            <WarningNotice translationKey="invoices.invoice-settings-modal.error-source-system-validation-failed" />
          ) : (
            <WarningNotice translationKey="invoices.invoice-settings-modal.error-saving-invoice-settings" />
          )
        }
        key="invoice-settings-modal"
      />,
      <ModalFooter key="invoice-settings-modal-footer">
        <Grid align={['middle-xs', 'between-xs']} horizontalGutter="small">
          <GridColumn width={['max']}>
            <InvoiceChangeLogNotice />
          </GridColumn>
          <GridColumn width={['xs']}>
            <LinkButton
              gaEvent="Link - close modal"
              buttonProps={{
                text: translate('invoices.invoice-settings-modal.cancel'),
                onClick: () => modalStore.closeModal(),
                iconLeft: '#arrow-left'
              }}
            />
          </GridColumn>
          <GridColumn width={['xs']}>
            <ButtonWithAnalytics
              gaEvent={`CTA - Form - Settings saved changes: ${this.props.invoiceSettingsModalStore.invoiceSettingsChanges()}`}
              buttonProps={{
                id: 'invoice-settings-panel-save-button',
                text: translate('invoices.invoice-settings-modal.save'),
                onClick: () => this.onSaveClick(),
                isLoading: modalStore.isSaving,
                disabled: modalStore.isSaving || panelStore.hasValidationError
              }}
            />
          </GridColumn>
        </Grid>
      </ModalFooter>
    ];
  }
}
